/**
 * Hajoca API Integration Utilities
 *
 * Adapter functions that bridge Hajoca API data structures to our application's
 * standard formats. All Hajoca-specific quirks and inconsistencies are handled here.
 */

import { formatAddress } from "./address-validation";
import type {
  HajocaSalesOrderInquiryResponse,
  HajocaShipment,
  HajocaLineItem,
} from "./types/hajoca";

/**
 * Format Hajoca shipment address for display
 *
 * Adapts Hajoca's address structure (streetLine1, zip) to our standard format
 * (streetAddress, zipCode) and calls our central formatAddress utility.
 *
 * @param shipment - Hajoca shipment object
 * @returns Formatted address string or null if no address available
 */
export function formatHajocaShipmentAddress(
  shipment: HajocaShipment
): string | null {
  const account = shipment.shipToAccount?.data?.attributes;
  const address = account?.address;

  if (!address) {
    return null;
  }

  // Adapt Hajoca field names to our standard address format
  return formatAddress({
    streetAddress: address.streetLine1,
    streetAddress2: address.streetLine2,
    city: address.city,
    state: address.state,
    zipCode: address.zipCode,
  });
}

/**
 * Get ship-to account name from Hajoca shipment
 *
 * @param shipment - Hajoca shipment object
 * @returns Account name or null if not available
 */
export function getHajocaShipmentAccountName(
  shipment: HajocaShipment
): string | null {
  return shipment.shipToAccount?.data?.attributes?.name ?? null;
}

/**
 * Format Hajoca shipment line item price for display
 *
 * Hajoca returns shipment line item prices as pre-formatted strings (e.g., "123.45").
 * This differs from order line items which are in cents.
 *
 * @param price - Price string from Hajoca
 * @returns Formatted price string (e.g., "123.45") or "0.00" if invalid
 */
export function formatHajocaShipmentLineItemPrice(price: string): string {
  if (!price || typeof price !== "string") {
    return "0.00";
  }

  const cleaned = price.trim();

  // If it's already a valid decimal string, return it
  if (/^\d+(\.\d{2})?$/.test(cleaned)) {
    return cleaned;
  }

  // Try to parse and format
  const parsed = parseFloat(cleaned);
  if (!isNaN(parsed) && isFinite(parsed)) {
    return parsed.toFixed(2);
  }

  return "0.00";
}

/**
 * Parse a Hajoca dollar-string price into integer cents
 *
 * Hajoca returns prices as decimal dollar strings, sometimes with three
 * decimals (e.g. "469.410" from /v1/inventory). The rest of the app stores
 * prices as integer cents, so convert at this boundary.
 *
 * @param price - Price string from Hajoca (e.g. "469.410")
 * @returns Price in cents (e.g. 46941), or null if unparseable
 */
export function parseHajocaPriceToCents(price: string): number | null {
  if (!price || typeof price !== "string") {
    return null;
  }

  const parsed = parseFloat(price.trim());
  if (isNaN(parsed) || !isFinite(parsed)) {
    return null;
  }

  return Math.round(parsed * 100);
}

/**
 * Parse a Hajoca quantity string into a number
 *
 * @param quantity - Quantity string from Hajoca (e.g. "5")
 * @returns Parsed quantity, or null if unparseable
 */
export function parseHajocaQuantity(quantity: string): number | null {
  if (!quantity || typeof quantity !== "string") {
    return null;
  }

  const parsed = parseFloat(quantity.trim());
  if (isNaN(parsed) || !isFinite(parsed)) {
    return null;
  }

  return parsed;
}

/**
 * Format Hajoca shipment quantity for display
 *
 * Hajoca returns quantities as strings. Parse them for clean display.
 *
 * @param quantity - Quantity string from Hajoca
 * @returns Formatted quantity string
 */
export function formatHajocaQuantity(quantity: string): string {
  if (!quantity || typeof quantity !== "string") {
    return "0";
  }

  const cleaned = quantity.trim();
  const parsed = parseFloat(cleaned);

  if (!isNaN(parsed) && isFinite(parsed)) {
    // Show decimals only if needed
    return parsed % 1 === 0 ? parsed.toString() : parsed.toFixed(2);
  }

  return quantity; // Return original if we can't parse
}

/**
 * Validate and extract fulfillment data from Hajoca inquiry response
 *
 * Provides safe access to nested response data with type checking.
 *
 * @param response - Hajoca sales order inquiry response
 * @returns Extracted data or null if invalid response
 */
export function extractHajocaFulfillmentData(
  response: HajocaSalesOrderInquiryResponse | null
) {
  if (!response?.data?.attributes) {
    return null;
  }

  const { lineItems = [], shipments = [] } = response.data.attributes;

  return {
    orderId: response.data.id,
    lineItems: lineItems as HajocaLineItem[],
    shipments: shipments as HajocaShipment[],
    hasShipments: shipments.length > 0,
    hasLineItems: lineItems.length > 0,
  };
}

/**
 * Type guard to check if response is a valid Hajoca inquiry response
 *
 * @param response - Response object to validate
 * @returns True if response matches expected structure
 */
export function isValidHajocaInquiryResponse(
  response: any
): response is HajocaSalesOrderInquiryResponse {
  return (
    response &&
    typeof response === "object" &&
    response.data &&
    typeof response.data === "object" &&
    response.data.type === "salesorder" &&
    response.data.attributes &&
    typeof response.data.attributes === "object"
  );
}

// ========================================
// CUSTOMER ID REGION PREFIXES
// ========================================
//
// Hajoca customer/account ids are a region prefix letter followed by the
// numeric customer number (e.g. "W181946"). The prefix is determined by the
// region of the profit center the customer belongs to (see the
// hajoca_branch_mapping table for profit center -> region).
//
// This is the single home for prefix logic — do not re-derive prefixes
// elsewhere.

/** Region (hajoca_branch_mapping.region) -> customer id prefix letter. */
export const HAJOCA_REGION_CUSTOMER_ID_PREFIXES: Record<string, string> = {
  eastern: "E",
  western: "W",
  central: "C",
  whci: "H",
  southeastern: "S",
  usa: "U",
};

/**
 * Resolve the customer-id prefix letter for a branch-mapping region.
 *
 * @param region - Region string as stored in hajoca_branch_mapping.region
 * @returns Prefix letter, or null for unknown regions
 */
export function regionToCustomerIdPrefix(region: string): string | null {
  return (
    HAJOCA_REGION_CUSTOMER_ID_PREFIXES[region.trim().toLowerCase()] ?? null
  );
}

/**
 * Build a full Hajoca customer id from a region and a customer number.
 *
 * Accepts either a bare number ("181946") or a full id whose prefix matches
 * the region ("W181946" / "w181946") — the latter is normalized. Rejects ids
 * whose prefix contradicts the region, unknown regions, and non-numeric input.
 *
 * @param region - Region string as stored in hajoca_branch_mapping.region
 * @param customerNumber - Numeric customer number, optionally prefixed
 * @returns The full customer id (e.g. "W181946"), or an error
 */
export function buildHajocaCustomerId(
  region: string,
  customerNumber: string,
): { customerId: string; error?: never } | { customerId?: never; error: string } {
  const prefix = regionToCustomerIdPrefix(region);
  if (!prefix) {
    return { error: `Unknown Hajoca region "${region}"` };
  }

  const input = customerNumber.trim().toUpperCase();
  if (!input) {
    return { error: "Customer number is required" };
  }

  const match = input.match(/^([A-Z])?(\d+)$/);
  if (!match) {
    return {
      error:
        "Customer number must be numeric, optionally preceded by a region letter",
    };
  }

  const [, inputPrefix, digits] = match;
  if (inputPrefix && inputPrefix !== prefix) {
    return {
      error: `Customer id prefix "${inputPrefix}" does not match the expected "${prefix}" for this region`,
    };
  }

  return { customerId: `${prefix}${digits}` };
}
